// @ts-check
/** @typedef {import('../types/api').Product} Product */

import { Edit, Eye, EyeOff, Layers, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import ProductEditModal from '../components/ProductEditModal';
import { BulkCollectionDialog, BulkPriceDialog, BulkShippingDialog } from '../components/products/BulkProductDialogs';
import BulkActionBar from '../components/ui/BulkActionBar';
import BulkConfirmModal from '../components/ui/BulkConfirmModal';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Modal from '../components/ui/Modal';
import { HeaderCheckbox, RowCheckbox } from '../components/ui/RowCheckbox';
import { useToast } from '../context/ToastContext';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import useRowSelection from '../hooks/useRowSelection';
import adminProductService from '../services/adminProductService';
import { reportBulkResult, runBulk } from '../services/bulkService';
import { downloadCsv } from '../utils/csv';

const BULK_PATH = '/api/admin/products/bulk';
const adminRole = () => {
  try {
    return JSON.parse(localStorage.getItem('adminUser') || '{}').role;
  } catch {
    return undefined;
  }
};

const ProductsPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [missingShippingOnly, setMissingShippingOnly] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(null); // 'price' | 'collection' | 'shipping' | 'delete'
  const [bulkBusy, setBulkBusy] = useState(false);
  const role = adminRole();
  const canEdit = ['super_admin', 'admin', 'manager'].includes(role);
  const canPriceOrDelete = ['super_admin', 'admin'].includes(role);

  const fetchProducts = useCallback(
    (page, limit) =>
      adminProductService.getAllProducts({
        page,
        limit,
        search: search || undefined,
        missing_shipping_data: missingShippingOnly || undefined,
      }),
    [search, missingShippingOnly],
  );

  const {
    items: products,
    loading,
    reset,
    sentinelRef,
  } = useInfiniteScroll(fetchProducts, {
    limit: 50,
    deps: [search, missingShippingOnly],
  });

  const selection = useRowSelection(
    products.map((p) => p.id),
    { resetKey: `${search}|${missingShippingOnly}` },
  );

  const finishBulk = () => {
    setBulkDialog(null);
    selection.clear();
    reset();
  };

  const bulkSimple = async (action, verb) => {
    setBulkBusy(true);
    try {
      const result = await runBulk(BULK_PATH, { ids: selection.selectedIds, action });
      reportBulkResult(toast, result, verb, 'product');
      finishBulk();
    } catch (error) {
      toast.error(error.message || 'Bulk update failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const exportSelected = () => {
    const chosen = products.filter((p) => selection.isSelected(p.id));
    downloadCsv('products.csv', [
      ['Name', 'Slug', 'Price', 'Sale price', 'Collection', 'Status'],
      ...chosen.map((p) => [p.name, p.slug, p.price, p.sale_price, p.collection_name, p.status]),
    ]);
  };

  const handleTogglePublish = async (productId, currentStatus) => {
    try {
      setActionLoading(productId);
      await adminProductService.togglePublish(productId, !currentStatus);
      reset();
      toast.success(currentStatus ? 'Product unpublished' : 'Product published successfully');
    } catch (error) {
      toast.error('Failed to update product status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteProduct = async (productId) => {
    setActionLoading(productId);
    try {
      await adminProductService.deleteProduct(productId);
      toast.success('Product deleted successfully');
      setDeleteConfirm(null);
      reset();
    } catch (error) {
      toast.error('Failed to delete product');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading && products.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-serif">Products</h1>
        <div className="flex gap-3">
          <button
            onClick={reset}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg border-input hover:bg-muted/50 disabled:opacity-50"
            title="Refresh products"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => {
              setSelectedProductId(null);
              setShowEditModal(true);
            }}
            className="px-6 py-2 bg-primary text-primary-foreground flex items-center gap-2 hover:bg-primary/90"
          >
            <Plus size={20} />
            ADD PRODUCT
          </button>
        </div>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70" size={20} />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 hover:text-foreground transition-colors"
              title="Clear search"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Products without an HS code or parcel weight can't have a shipping label
            generated, so they need to be findable before a carrier API goes live. */}
        <label className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={missingShippingOnly}
            onChange={(e) => setMissingShippingOnly(e.target.checked)}
          />
          Show only products missing shipping data (HS code or weight)
        </label>
      </div>

      <BulkActionBar count={selection.count} onClear={selection.clear}>
        <BulkActionBar.Button onClick={() => bulkSimple('publish', 'published')} disabled={bulkBusy}>
          Publish
        </BulkActionBar.Button>
        <BulkActionBar.Button onClick={() => bulkSimple('unpublish', 'unpublished')} disabled={bulkBusy}>
          Unpublish
        </BulkActionBar.Button>
        {canEdit && (
          <>
            <BulkActionBar.Button onClick={() => setBulkDialog('collection')}>Set collection</BulkActionBar.Button>
            <BulkActionBar.Button onClick={() => setBulkDialog('shipping')}>Shipping data</BulkActionBar.Button>
          </>
        )}
        {canPriceOrDelete && (
          <BulkActionBar.Button onClick={() => setBulkDialog('price')}>Change price</BulkActionBar.Button>
        )}
        <BulkActionBar.Button onClick={exportSelected}>Export CSV</BulkActionBar.Button>
        {canPriceOrDelete && (
          <BulkActionBar.Button tone="danger" onClick={() => setBulkDialog('delete')}>
            Delete
          </BulkActionBar.Button>
        )}
      </BulkActionBar>

      <div className="bg-card border rounded-lg border-border shadow-sm overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="pl-6 py-3">
                <HeaderCheckbox selection={selection} />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Image</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Collection</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Status</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {products.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                  No products found
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <tr key={product.id} className="hover:bg-muted/50">
                  <td className="pl-6 py-4">
                    <RowCheckbox selection={selection} id={product.id} label={`Select ${product.name}`} />
                  </td>
                  <td className="px-6 py-4">
                    <img
                      src={
                        product.image_urls?.[0] ||
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23f3f4f6" width="80" height="80"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="10"%3ENo Image%3C/text%3E%3C/svg%3E'
                      }
                      alt={product.name}
                      className="w-16 h-16 object-cover"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-foreground">{product.name}</div>
                    <div className="text-xs text-muted-foreground">{product.slug}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground">
                    {'\u20B9'}
                    {Number(product.price).toLocaleString('en-IN')}
                    {product.sale_price && (
                      <span className="ml-2 text-red-600 dark:text-red-400">
                        {'\u20B9'}
                        {Number(product.sale_price).toLocaleString('en-IN')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{product.collection_name || '-'}</td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleTogglePublish(product.id, product.status === 'published')}
                      disabled={actionLoading === product.id}
                      className={`inline-flex items-center px-3 py-1 rounded-md text-xs transition-opacity ${
                        product.status === 'published'
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                          : 'bg-muted text-foreground'
                      } ${actionLoading === product.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {actionLoading === product.id ? (
                        <span className="flex items-center">
                          <div className="w-3 h-3 border-2 border-muted-foreground/50 border-t-transparent rounded-full animate-spin mr-1"></div>
                          Updating...
                        </span>
                      ) : product.status === 'published' ? (
                        <>
                          <Eye size={14} className="mr-1" /> Published
                        </>
                      ) : (
                        <>
                          <EyeOff size={14} className="mr-1" /> {product.status === 'archived' ? 'Archived' : 'Draft'}
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => {
                          setSelectedProductId(product.id);
                          setShowEditModal(true);
                        }}
                        className="text-muted-foreground hover:text-foreground"
                        title="Edit"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => navigate(`/products/${product.id}/materials`)}
                        className="text-muted-foreground hover:text-foreground"
                        title="Materials / BOM"
                      >
                        <Layers size={18} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(product.id)}
                        className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="py-4 flex justify-center">
        {loading && products.length > 0 && <LoadingSpinner size="sm" />}
      </div>

      {/* Product Edit Modal */}
      {showEditModal && (
        <ProductEditModal
          productId={selectedProductId}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProductId(null);
          }}
          onSave={() => {
            reset();
          }}
        />
      )}

      {bulkDialog === 'price' && (
        <BulkPriceDialog
          count={selection.count}
          ids={selection.selectedIds}
          onClose={() => setBulkDialog(null)}
          onDone={finishBulk}
        />
      )}
      {bulkDialog === 'collection' && (
        <BulkCollectionDialog
          count={selection.count}
          ids={selection.selectedIds}
          onClose={() => setBulkDialog(null)}
          onDone={finishBulk}
        />
      )}
      {bulkDialog === 'shipping' && (
        <BulkShippingDialog
          count={selection.count}
          ids={selection.selectedIds}
          onClose={() => setBulkDialog(null)}
          onDone={finishBulk}
        />
      )}
      {bulkDialog === 'delete' && (
        <BulkConfirmModal
          title="Delete products"
          message="They leave the catalog and every variant is marked out of stock. Deleted products can be restored from Recovery."
          count={selection.count}
          confirmLabel="Delete"
          danger
          requireCount
          busy={bulkBusy}
          onConfirm={() => bulkSimple('delete', 'deleted')}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <Modal maxWidth="md" fullHeight={false} closeOnBackdrop={false} className="rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Delete Product?</h3>
          <p className="text-muted-foreground mb-6">
            This product will be soft-deleted. It will no longer appear in the catalog, and all variants will be marked
            as out of stock. This action can be reversed by contacting support.
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-4 py-2 border border-input rounded-lg hover:bg-muted/50"
              disabled={actionLoading === deleteConfirm}
            >
              Cancel
            </button>
            <button
              onClick={() => handleDeleteProduct(deleteConfirm)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              disabled={actionLoading === deleteConfirm}
            >
              {actionLoading === deleteConfirm ? 'Deleting...' : 'Delete Product'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ProductsPage;
